/**
 * EVO — yon menyu nishonlari.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN INLINE SVG, KUTUBXONA EMAS                                     │
 * │                                                                           │
 * │ Nishon kutubxonasi (lucide, heroicons) 300+ kB keltiradi va undan o'n     │
 * │ bir tasi ishlatiladi. Tree-shaking yordam beradi, lekin baribir yangi     │
 * │ bog'liqlik: versiya yangilanishi, nomlar o'zgarishi, bundle'da            │
 * │ kutilmagan o'sish.                                                        │
 * │                                                                           │
 * │ Bu yerda 11 ta nishon bor (sanalgan: `PATHS` kalitlari) va ular hech      │
 * │ qachon o'zgarmaydi. Yangi bo'lim qo'shilganda bitta `path` yoziladi.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Barcha nishonlar bir xil grammatikada: 24×24 koordinata to'ri, faqat chiziq
 * (`stroke`), to'ldirish yo'q, qalinlik 1.75. Aralash uslub (bir joyda to'la,
 * bir joyda chiziq) menyuni tartibsiz ko'rsatadi.
 */
import type { ReactNode } from 'react';

export type NavIconName =
  | 'home'
  | 'catalog'
  | 'warehouse'
  | 'import'
  | 'sales'
  | 'loyalty'
  | 'finance'
  | 'reports'
  | 'accounting'
  | 'security'
  | 'system';

/**
 * Nishon yo'llari.
 *
 * Har biri O'ZI ifodalaydigan tushunchaga yaqin bo'lishi kerak, chiroyli
 * bo'lishi emas: `warehouse` — quti, `finance` — hamyon, `loyalty` — medal.
 * Foydalanuvchi shaklni matn bilan bir marta bog'laydi va keyin faqat
 * shaklni qidiradi.
 */
const PATHS: Record<NavIconName, ReactNode> = {
  // Uy — bosh sahifa
  home: <path d="M3 10.5 12 3l9 7.5M5.5 9.5V20h13V9.5M9.5 20v-6h5v6" />,

  // Yorliqli quti — mahsulot va kontragent kartochkalari
  catalog: (
    <>
      <path d="M4 6.5A1.5 1.5 0 0 1 5.5 5H10l1.5 2h7A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5Z" />
      <path d="M8 12h8" />
    </>
  ),

  // Ustma-ust qutilar — ombor qoldig'i
  warehouse: (
    <>
      <path d="M3 8.5 12 4l9 4.5-9 4.5-9-4.5Z" />
      <path d="M3 12.5 12 17l9-4.5M3 16.5 12 21l9-4.5" />
    </>
  ),

  // Kema/konteyner — import va xarid
  import: (
    <>
      <path d="M3 16.5c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0" />
      <path d="M5 13V8.5h14V13" />
      <path d="M9 8.5V6h6v2.5M12 4v2" />
    </>
  ),

  // Chek — savdo hujjatlari
  sales: (
    <>
      <path d="M6 3.5h12v17l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5Z" />
      <path d="M9.5 8h5M9.5 12h5" />
    </>
  ),

  // Medal — usta dasturi (ball va sovg'a)
  loyalty: (
    <>
      <circle cx="12" cy="9" r="5" />
      <path d="M9 13.5 7.5 21l4.5-2.5 4.5 2.5L15 13.5" />
    </>
  ),

  // Hamyon — kassa va to'lov
  finance: (
    <>
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h15A1.5 1.5 0 0 1 21 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5Z" />
      <path d="M3 10.5h18M16.5 14h1.5" />
    </>
  ),

  // Ustunli diagramma — hisobotlar
  reports: (
    <>
      <path d="M4 20V4M4 20h16" />
      <path d="M8 20v-6M12.5 20V9M17 20v-8" />
    </>
  ),

  /*
   * Ochiq daftar — buxgalteriya.
   *
   * `finance` (hamyon) va `reports` (diagramma) ATAYLAB qayta ishlatilmadi:
   * yon menyu yig'ilganda foydalanuvchi bo'limni matndan emas, SHAKLDAN
   * topadi (`config/menu.ts` dagi `icon` izohi). Ikki guruhda bir xil nishon
   * bo'lsa, bu qidiruv ishlamay qoladi. Daftar esa aynan shu bo'limning
   * ma'nosi: ikki yozuvli DAFTAR.
   */
  accounting: (
    <>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5Z" />
      <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5a1.5 1.5 0 0 0 1.5-1.5Z" />
      <path d="M7 9h1.5M15.5 9H17M7 13h1.5M15.5 13H17" />
    </>
  ),

  /*
   * Qalqon — «Xavfsizlik».
   *
   * `system` (tishli g'ildirak) qayta ishlatilmadi va bu shu fayldagi
   * qoidaning aynan o'zi (`accounting` izohiga qarang): ikki guruhda bir xil
   * shakl bo'lsa, yig'ilgan menyudagi «shaklni tanish» ishlamay qoladi.
   * Bu yerda farq ayniqsa muhim — ikkala bo'lim ham `audit.view` bilan
   * ochiladigan rollarda YONMA-YON turadi.
   */
  security: (
    <>
      <path d="M12 3.5 5 6.2v5.4c0 4.2 2.8 7.5 7 8.9 4.2-1.4 7-4.7 7-8.9V6.2Z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </>
  ),

  // Tishli g'ildirak — sozlamalar
  system: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" />
    </>
  ),
};

/**
 * `aria-hidden` — nishon matnni TAKRORLAYDI, o'rnini bosmaydi.
 *
 * Yonida har doim bo'lim nomi turadi, ya'ni ekran o'quvchi uchun nishon
 * ortiqcha shovqin. Uni e'lon qilish «Home rasm, Bosh sahifa» deb ikki
 * marta o'qilishiga olib kelardi.
 */
export function NavIcon({ name, className }: {
  readonly name: NavIconName;
  readonly className?: string;
}): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className ?? 'h-4.5 w-4.5 shrink-0'}
    >
      {PATHS[name]}
    </svg>
  );
}

/** Guruh ochiq/yopiqligini ko'rsatuvchi burchak. */
export function Chevron({ open }: { readonly open: boolean }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={`h-3.5 w-3.5 shrink-0 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}
